import { Link, useNavigate } from 'react-router-dom';
import { logger } from '../utils/logger';
import { ShoppingCart, Trash2, CreditCard, Coins, ArrowRight, Tag, Check, X, Loader2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { useState, useEffect, useCallback } from 'react';
import { prepareCheckoutWindow, completeCheckoutRedirect, closeCheckoutWindow, isStandalonePWA } from '../utils/pwaCheckout';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const DISCOUNT_STORAGE_KEY = 'qjones_cart_discount';

export default function CartPage() {
  const navigate = useNavigate();
  const { cart, removeFromCart, loading } = useCart();
  const { isAuthenticated, user } = useAuth();
  const [checkingOut, setCheckingOut] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');

  // Discount code state
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null); // { code, discount_percent }
  const [codeError, setCodeError] = useState('');

  // Load persisted discount on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(DISCOUNT_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.code && parsed?.discount_percent) {
          setAppliedDiscount(parsed);
        }
      }
    } catch (e) {
      logger.error('Failed to load stored discount', e);
    }
  }, []);

  // Re-validate persisted code whenever the user or cart total changes
  const revalidateStoredCode = useCallback(async () => {
    if (!appliedDiscount?.code) return;
    try {
      const res = await axios.post(`${API_URL}/discount/validate`, {
        code: appliedDiscount.code,
        email: user?.email || undefined,
      });
      if (res.data?.valid) {
        const next = { code: res.data.code, discount_percent: res.data.discount_percent };
        setAppliedDiscount(next);
        localStorage.setItem(DISCOUNT_STORAGE_KEY, JSON.stringify(next));
      }
    } catch (err) {
      // Code is no longer valid — remove it silently
      setAppliedDiscount(null);
      localStorage.removeItem(DISCOUNT_STORAGE_KEY);
      toast.info('Your discount code is no longer valid and has been removed.');
    }
  }, [appliedDiscount?.code, user?.email]);

  useEffect(() => {
    revalidateStoredCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  // Auto-surface a fan's unused welcome/comeback discount instead of making
  // them dig the code out of an email and type it in manually. Skips if a
  // code is already applied or was already stored (don't clobber a choice).
  useEffect(() => {
    if (!user?.email || appliedDiscount || localStorage.getItem(DISCOUNT_STORAGE_KEY)) return;
    axios.get(`${API_URL}/discount/mine`, { params: { email: user.email } })
      .then((res) => {
        if (res.data?.has_code) {
          const next = { code: res.data.code, discount_percent: res.data.discount_percent };
          setAppliedDiscount(next);
          localStorage.setItem(DISCOUNT_STORAGE_KEY, JSON.stringify(next));
          toast.success(`Welcome! ${next.discount_percent}% off has been applied for you.`);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const handleRemove = async (itemId) => {
    try {
      await removeFromCart(itemId);
      toast.success('Item removed from cart');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const handleApplyCode = async (e) => {
    e?.preventDefault();
    setCodeError('');

    if (appliedDiscount) {
      setCodeError('A discount is already applied');
      return;
    }

    const code = codeInput.trim();
    if (!code) {
      setCodeError('Enter a code');
      return;
    }

    setApplyingCode(true);
    try {
      const res = await axios.post(`${API_URL}/discount/validate`, {
        code,
        email: user?.email || undefined,
      });
      if (res.data?.valid) {
        const applied = {
          code: res.data.code,
          discount_percent: res.data.discount_percent,
        };
        setAppliedDiscount(applied);
        localStorage.setItem(DISCOUNT_STORAGE_KEY, JSON.stringify(applied));
        setCodeInput('');
        setShowCodeInput(false);
        toast.success(`${applied.discount_percent}% off applied!`);
      } else {
        setCodeError('Code invalid, expired, or already used');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Code invalid, expired, or already used';
      setCodeError(detail);
    } finally {
      setApplyingCode(false);
    }
  };

  const handleRemoveCode = () => {
    setAppliedDiscount(null);
    localStorage.removeItem(DISCOUNT_STORAGE_KEY);
    setCodeError('');
    toast.info('Discount code removed');
  };

  const subtotal = cart.total || 0;
  const discountAmount = appliedDiscount
    ? Math.round(subtotal * (appliedDiscount.discount_percent / 100) * 100) / 100
    : 0;
  const total = Math.max(0, Math.round((subtotal - discountAmount) * 100) / 100);

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to checkout');
      navigate('/login');
      return;
    }

    if (cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setCheckingOut(true);
    // iOS standalone PWAs silently drop window.location.href to external URLs.
    // Open the Stripe tab synchronously inside the click gesture, then redirect
    // it once the backend returns the session URL.
    const pwaWindow = prepareCheckoutWindow();
    try {
      const response = await axios.post(`${API_URL}/checkout/create`, {
        origin_url: window.location.origin,
        payment_method: paymentMethod,
        discount_code: appliedDiscount?.code || '',
      });

      // Clear stored code on successful redirect — Stripe will consume it
      if (appliedDiscount) {
        localStorage.removeItem(DISCOUNT_STORAGE_KEY);
      }

      if (isStandalonePWA()) {
        toast.info('Opening secure checkout in your browser. Come back here when done.');
      }
      completeCheckoutRedirect(pwaWindow, response.data.url);
    } catch (error) {
      closeCheckoutWindow(pwaWindow);
      logger.error('Checkout error:', error);
      const detail = error.response?.data?.detail || 'Failed to create checkout session';
      // If the discount code was rejected server-side, drop it so retry works
      if (typeof detail === 'string' && detail.toLowerCase().includes('discount')) {
        setAppliedDiscount(null);
        localStorage.removeItem(DISCOUNT_STORAGE_KEY);
      }
      toast.error(detail);
    } finally {
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="main-content pt-24" data-testid="cart-page">
      <div className="container py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <ShoppingCart className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Your Cart</h1>
            <p className="text-gray-400">{cart.items.length} items</p>
          </div>
        </div>

        {cart.items.length === 0 ? (
          <div className="empty-state glass-card py-16">
            <ShoppingCart className="empty-state-icon w-16 h-16" />
            <h3 className="text-xl font-semibold mb-2">Your cart is empty</h3>
            <p className="text-gray-400 mb-6">Start shopping to add items to your cart</p>
            <Link to="/music" className="btn-primary" data-testid="browse-music-link">
              Browse Music
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map((item) => (
                <div 
                  key={`${item.item_id}-${item.item_type}`}
                  className="glass-card p-4 flex gap-4"
                  data-testid={`cart-item-${item.item_id}`}
                >
                  <img 
                    src={item.details?.cover_url || item.details?.image_url}
                    alt={item.details?.title || item.details?.name}
                    className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                  />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <h3 className="font-semibold truncate">
                          {item.details?.title || item.details?.name}
                        </h3>
                        <p className="text-sm text-gray-400 capitalize">
                          {item.item_type === 'token' 
                            ? `${item.token_amount}% Ownership Token` 
                            : item.item_type}
                          {item.size && ` • Size: ${item.size}`}
                        </p>
                        {item.item_type !== 'token' && item.quantity > 1 && (
                          <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                        )}
                      </div>
                      
                      <div className="text-right flex-shrink-0">
                        <p className="font-semibold text-purple-400">
                          ${item.subtotal.toFixed(2)}
                        </p>
                        <button
                          onClick={() => handleRemove(item.item_id)}
                          className="text-red-400 hover:text-red-300 mt-2"
                          data-testid={`remove-${item.item_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div>
              <div className="glass-card p-6 sticky top-24">
                <h2 className="text-xl font-bold mb-6">Order Summary</h2>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-gray-400">
                    <span>Subtotal</span>
                    <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>Processing Fee</span>
                    <span>$0.00</span>
                  </div>
                  {appliedDiscount && (
                    <div className="flex justify-between text-green-400" data-testid="cart-discount-line">
                      <span>Discount ({appliedDiscount.discount_percent}% off)</span>
                      <span>-${discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-white/10 pt-3 flex justify-between text-lg font-semibold">
                    <span>Total</span>
                    <span className="text-purple-400" data-testid="cart-total">${total.toFixed(2)}</span>
                  </div>
                </div>

                {/* Discount code section — right above the Checkout button */}
                <div className="mb-6" data-testid="discount-section">
                  {appliedDiscount ? (
                    <div
                      className="flex items-center justify-between gap-2 p-3 rounded-lg border border-green-500/40 bg-green-500/10"
                      data-testid="applied-discount-chip"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-green-300 truncate">
                            {appliedDiscount.code}
                          </p>
                          <p className="text-xs text-green-400/80">
                            {appliedDiscount.discount_percent}% off applied
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveCode}
                        className="text-green-300 hover:text-white p-1"
                        aria-label="Remove discount code"
                        data-testid="remove-discount-btn"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : showCodeInput ? (
                    <form onSubmit={handleApplyCode} className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={codeInput}
                          onChange={(e) => {
                            setCodeInput(e.target.value.toUpperCase());
                            if (codeError) setCodeError('');
                          }}
                          placeholder="Enter code"
                          maxLength={50}
                          className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 uppercase tracking-wide"
                          data-testid="discount-code-input"
                          autoFocus
                          disabled={applyingCode}
                        />
                        <button
                          type="submit"
                          disabled={applyingCode || !codeInput.trim()}
                          className="px-4 py-2 rounded-lg bg-purple-500 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium flex items-center gap-2"
                          data-testid="apply-discount-btn"
                        >
                          {applyingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                        </button>
                      </div>
                      {codeError && (
                        <p className="text-xs text-red-400" data-testid="discount-error">
                          {codeError}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setShowCodeInput(false);
                          setCodeInput('');
                          setCodeError('');
                        }}
                        className="text-xs text-gray-500 hover:text-gray-300"
                      >
                        Cancel
                      </button>
                    </form>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowCodeInput(true)}
                      className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed border-white/15 hover:border-purple-500/60 hover:bg-purple-500/5 text-gray-300 hover:text-white text-sm transition-colors"
                      data-testid="have-a-code-toggle"
                    >
                      <Tag className="w-4 h-4" />
                      Have a code?
                    </button>
                  )}
                </div>

                {/* Payment Method */}
                <div className="mb-6">
                  <p className="text-sm font-medium text-gray-400 mb-3">Payment Method</p>
                  <div className="space-y-2">
                    <button
                      onClick={() => setPaymentMethod('card')}
                      className={`w-full p-3 rounded-lg flex items-center gap-3 border ${
                        paymentMethod === 'card' 
                          ? 'border-purple-500 bg-purple-500/10' 
                          : 'border-white/10 hover:border-white/20'
                      }`}
                      data-testid="payment-card"
                    >
                      <CreditCard className="w-5 h-5" />
                      <span>Credit/Debit Card</span>
                    </button>
                    <button
                      onClick={() => setPaymentMethod('both')}
                      className={`w-full p-3 rounded-lg flex items-center gap-3 border ${
                        paymentMethod === 'both' 
                          ? 'border-cyan-500 bg-cyan-500/10' 
                          : 'border-white/10 hover:border-white/20'
                      }`}
                      data-testid="payment-crypto"
                    >
                      <Coins className="w-5 h-5" />
                      <span>Card or Crypto (USDC)</span>
                    </button>
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  disabled={checkingOut || cart.items.length === 0}
                  className="w-full btn-primary py-4 flex items-center justify-center gap-2"
                  data-testid="checkout-button"
                >
                  {checkingOut ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Checkout <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center mt-4">
                  Secure checkout powered by Stripe
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
